<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center gap-3 mb-3 hms-page-header">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back"><i class="mdi mdi-arrow-left"></i></a>
        <h4 class="hms-page-title m-0"><i class="mdi mdi-tune-variant me-2"></i>Workflow &amp; Billing Settings</h4>
      </div>
      <div class="hms-muted-sm mb-3">Each facility configures its own departments and queue-routing rules here — no code changes needed.</div>

      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab==='bd' }" @click="tab='bd'"><i class="mdi mdi-office-building me-1"></i>Billing Departments</button>
        <button class="hms-tab" :class="{ active: tab==='qr' }" @click="tab='qr'"><i class="mdi mdi-arrow-decision me-1"></i>Save-&amp;-Queue Routing</button>
      </div>

      <!-- ============ BILLING DEPARTMENTS ============ -->
      <div v-if="tab==='bd'" class="hms-card">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="hms-muted">Defines how invoice-item categories roll up into your departments for the Revenue-by-Department view.</div>
          <button class="hms-btn hms-btn-primary" @click="newBD"><i class="mdi mdi-plus"></i> Add Department</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Code</th><th>Department</th><th>Item categories</th><th>Order</th><th>Active</th><th></th></tr></thead>
          <tbody>
            <tr v-for="d in departments" :key="d.dept_id">
              <td><code>{{ d.department_code }}</code></td>
              <td>{{ d.department_name }}</td>
              <td class="hms-muted-sm">{{ d.item_categories }}</td>
              <td>{{ d.sort_order }}</td>
              <td><span class="hms-badge" :class="d.is_active==1?'hms-badge-success':'hms-badge-neutral'">{{ d.is_active==1?'Active':'Off' }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="editBD(d)"><i class="mdi mdi-pencil"></i></button>
                  <button class="hms-btn hms-btn-red hms-btn-sm ms-1" @click="deleteBD(d)" v-if="d.is_active==1"><i class="mdi mdi-archive"></i></button>
              </td>
            </tr>
            <tr v-if="!departments.length"><td colspan="6" class="hms-muted text-center py-3">No departments configured.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ============ QUEUE ROUTING ============ -->
      <div v-if="tab==='qr'" class="hms-card">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="hms-muted">Defines the destinations available in the "Save and queue to ___" dropdown at each service point.</div>
          <button class="hms-btn hms-btn-primary" @click="newQR"><i class="mdi mdi-plus"></i> Add Routing Rule</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>From</th><th>To</th><th>Dropdown label</th><th>Order</th><th>Active</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in rules" :key="r.rule_id">
              <td><code>{{ r.from_point }}</code></td>
              <td><code>{{ r.to_point }}</code></td>
              <td>{{ r.label }}</td>
              <td>{{ r.sort_order }}</td>
              <td><span class="hms-badge" :class="r.is_active==1?'hms-badge-success':'hms-badge-neutral'">{{ r.is_active==1?'Active':'Off' }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="editQR(r)"><i class="mdi mdi-pencil"></i></button>
                  <button class="hms-btn hms-btn-red hms-btn-sm ms-1" @click="deleteQR(r)" v-if="r.is_active==1"><i class="mdi mdi-archive"></i></button>
              </td>
            </tr>
            <tr v-if="!rules.length"><td colspan="6" class="hms-muted text-center py-3">No routing rules configured.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- Edit modal -->
      <div v-if="modal" class="hms-modal-overlay" @click="modal=null">
        <div class="hms-modal" @click.stop>
          <div class="hms-modal-head">
            <h5 v-if="modal==='bd'">{{ form.dept_id ? 'Edit' : 'Add' }} Billing Department</h5>
            <h5 v-else>{{ form.rule_id ? 'Edit' : 'Add' }} Routing Rule</h5>
            <button class="hms-icon-btn" @click="modal=null">&times;</button>
          </div>

          <div v-if="modal==='bd'" class="row g-3 p-2">
            <div class="col-md-4"><label class="hms-label">Code *</label><input class="hms-input" v-model="form.department_code" placeholder="e.g. LAB" /></div>
            <div class="col-md-8"><label class="hms-label">Display name *</label><input class="hms-input" v-model="form.department_name" placeholder="Laboratory" /></div>
            <div class="col-12"><label class="hms-label">Item categories (comma-separated) *</label>
              <input class="hms-input" v-model="form.item_categories" placeholder="lab,radiology" />
              <div class="hms-muted-sm mt-1">Use any of: <code>consultation, procedure, lab, radiology, pharmacy, ward, theatre, other</code>. Merge two into one department by listing both (e.g. <code>lab,radiology</code> = "Diagnostics").</div>
            </div>
            <div class="col-md-4"><label class="hms-label">Sort order</label><input class="hms-input" type="number" v-model.number="form.sort_order" /></div>
            <div class="col-md-4 d-flex align-items-end"><label class="hms-check"><input type="checkbox" v-model="form.is_active" :true-value="1" :false-value="0" /> Active</label></div>
          </div>

          <div v-else class="row g-3 p-2">
            <div class="col-md-6"><label class="hms-label">From point *</label>
              <input class="hms-input" v-model="form.from_point" placeholder="consultation, lab, pharmacy…" list="rp-from" />
              <datalist id="rp-from">
                <option v-for="x in commonPoints" :key="x" :value="x"></option>
              </datalist>
            </div>
            <div class="col-md-6"><label class="hms-label">To point *</label>
              <input class="hms-input" v-model="form.to_point" placeholder="pharmacy, billing, exit…" list="rp-to" />
              <datalist id="rp-to">
                <option v-for="x in commonPoints" :key="x" :value="x"></option>
              </datalist>
            </div>
            <div class="col-12"><label class="hms-label">Dropdown label *</label><input class="hms-input" v-model="form.label" placeholder="Pharmacy" /></div>
            <div class="col-md-4"><label class="hms-label">Sort order</label><input class="hms-input" type="number" v-model.number="form.sort_order" /></div>
            <div class="col-md-4 d-flex align-items-end"><label class="hms-check"><input type="checkbox" v-model="form.is_active" :true-value="1" :false-value="0" /> Active</label></div>
          </div>

          <div class="d-flex justify-content-end gap-2 p-2">
            <button class="hms-btn hms-btn-blue" @click="modal=null">Cancel</button>
            <button class="hms-btn hms-btn-primary" :disabled="saving" @click="save"><i class="mdi mdi-content-save"></i> {{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>

      <transition name="hms-toast"><div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">{{ toastMsg }}</div></transition>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "WorkflowSettings",
  components: { Navigation },
  data() {
    return {
      tab: 'bd',
      departments: [], rules: [],
      modal: null, form: {}, saving: false,
      commonPoints: ['reception','triage','consultation','lab','radiology','pharmacy','billing','admission','ward','casualty','exit'],
      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.loadAll();
  },
  methods: {
    async loadAll() {
      try {
        var [a,b] = await Promise.all([
          executeJson({ function: 'getBillingDepartments' }, hms),
          executeJson({ function: 'getQueueRoutingRules' }, hms)
        ]);
        this.departments = (a.data && a.data.success) ? (a.data.data.departments || []) : [];
        this.rules       = (b.data && b.data.success) ? (b.data.data.rules || []) : [];
      } catch (e) { this.showToast("Failed to load — admin access required.", true); }
    },
    newBD() { this.form = { dept_id: 0, department_code: '', department_name: '', item_categories: '', sort_order: 0, is_active: 1 }; this.modal = 'bd'; },
    editBD(d) { this.form = Object.assign({}, d); this.modal = 'bd'; },
    async deleteBD(d) { if (!confirm("Deactivate " + d.department_name + "?")) return; await executeJson({ function: 'deleteBillingDepartment', dept_id: d.dept_id }, hms); this.loadAll(); },
    newQR() { this.form = { rule_id: 0, from_point: '', to_point: '', label: '', sort_order: 0, is_active: 1 }; this.modal = 'qr'; },
    editQR(r) { this.form = Object.assign({}, r); this.modal = 'qr'; },
    async deleteQR(r) { if (!confirm("Deactivate this routing rule?")) return; await executeJson({ function: 'deleteQueueRoutingRule', rule_id: r.rule_id }, hms); this.loadAll(); },
    async save() {
      this.saving = true;
      try {
        var fn = this.modal === 'bd' ? 'saveBillingDepartment' : 'saveQueueRoutingRule';
        var r = await executeJson(Object.assign({ function: fn }, this.form), hms);
        if (r.data && r.data.success) { this.modal = null; await this.loadAll(); this.showToast("Saved."); }
        else this.showToast((r.data && r.data.message) || "Save failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.saving = false;
    },
    showToast(msg, err) {
      this.toastMsg = msg; this.toastError = !!err;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this; this.toastTimer = setTimeout(function(){ self.toastMsg = ''; }, 2400);
    }
  }
};
</script>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 16px 40px; }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; }
.hms-back:hover { color: var(--hms-primary); }
.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-tabs { display: flex; gap: 8px; flex-wrap: wrap; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-s); border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.hms-tab.active { background: var(--hms-primary); color: #fff; border-color: var(--hms-primary); }
.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 12px; color: var(--hms-h); font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-check { color: var(--hms-h); font-size: 13px; }
.hms-btn { border: none; border-radius: 8px; padding: 7px 14px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-btn-red { background: rgba(220,53,69,.12); color: var(--hms-danger); }
.hms-badge { padding: 2px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-s); }
.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 60px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 16px; width: 100%; max-width: 640px; }
.hms-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 6px 8px; }
.hms-icon-btn { background: none; border: none; color: var(--hms-s); font-size: 22px; cursor: pointer; }
.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); padding: 12px 18px; border-radius: 10px; z-index: 1000; font-size: 13px; color: var(--hms-h); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.text-center { text-align: center; }
.py-3 { padding: 16px 0; }
code { background: var(--hms-bg3); padding: 1px 4px; border-radius: 3px; font-size: 12px; }
</style>
